// ─── A container's run, as points on a map ───────────────────────────────────
//
// The map twin of `routePoints` in services/routeDistanceService.js, and it
// reads the same stops the mileage badge is measured over: the ones the
// container actually makes, in route order, that have a location saved on them.
//
// Deliberately pure and free of React: the container's own page and the
// planning board's preview both draw the same run, and the only thing they
// disagree about is how much room they have for it.

import { routeStopsOf } from "./containerLineSteps";
import type {
  ContainerRouteStop,
  ContainerStopRole,
} from "../types/containerOrders";

/** One located stop, ready to be drawn. */
export interface RouteMapStop {
  id: string;
  /**
   * The number this stop wears on its card below — its position on the *whole*
   * route, skipped stops included. Counting only the located ones would put a
   * "2" on the map next to a card numbered 3, which is precisely the reading
   * the numbers exist to make possible.
   */
  index: number;
  role: ContainerStopRole;
  kind: ContainerRouteStop["kind"];
  label: string | null;
  /**
   * The place, in as few words as a pin can carry — the town, normally.
   *
   * Not an address: a pin has room for "Peshawar" and none for "67/1, Korangi
   * Road, R3JJ+F64, KPT Interchange, Qayyumabad". What a reader wants off a map
   * is which town this stop is in, and the full address is on the card that the
   * map sits above.
   *
   * Falls back through the county to the company's own name, because a pin
   * reading nothing but a number is a pin that has to be hovered to be read.
   */
  place: string | null;
  lat: number;
  lng: number;
}

export interface RouteMapRun {
  stops: RouteMapStop[];
  /**
   * Stops the container makes that have no saved location.
   *
   * Counted rather than dropped in silence: a run drawn as two points when it
   * has three is a wrong map, and the card says so instead of showing it.
   */
  missing: number;
}

/**
 * The colour each part of the route is drawn in — the hex behind the accent
 * classes the stop cards use, because an SVG pin cannot wear a Tailwind class.
 */
export const ROUTE_STOP_COLOR: Record<ContainerStopRole, string> = {
  terminalPickup: "#0B0F0E", // ink
  loadingSite: "#C97A3C", // copper
  terminalDropoff: "#0B0F0E", // ink
  custom: "#D97706", // amber-600
};

/** The route line itself, and the halo that keeps it legible over dark map. */
export const ROUTE_LINE_COLOR = "#C97A3C";

const located = (value: unknown, limit: number): value is number =>
  typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= limit;

/**
 * The located stops of this container's run, in order.
 *
 * `(0, 0)` is rejected the way the server rejects it: it is what an unset pair
 * turns into on older records, and it is in the Gulf of Guinea rather than at a
 * terminal.
 */
export function routeMapRun(
  line: Parameters<typeof routeStopsOf>[0],
): RouteMapRun {
  const stops: RouteMapStop[] = [];
  let missing = 0;

  routeStopsOf(line).forEach((stop, position) => {
    if (stop.enabled === false) return;

    const lat = stop.address?.coordinates?.latitude;
    const lng = stop.address?.coordinates?.longitude;
    if (!located(lat, 90) || !located(lng, 180) || (lat === 0 && lng === 0)) {
      missing += 1;
      return;
    }

    stops.push({
      id: String(stop._id ?? position),
      index: position + 1,
      role: stop.role,
      kind: stop.kind,
      label: stop.label?.trim() || null,
      place:
        stop.address?.city?.trim() ||
        stop.address?.state?.trim() ||
        stop.companyName?.trim() ||
        null,
      lat,
      lng,
    });
  });

  return { stops, missing };
}

/** The least a run has to be to be measured or drawn. */
export interface RoutePoint {
  lat: number;
  lng: number;
}

/**
 * Points and order as one string — equal fingerprints, equal route.
 *
 * The same string the server builds (`routeFingerprint` in
 * routeDistanceService), to the same five decimal places, so a route measured
 * on one side is recognisable as the same route on the other.
 */
export const routeMapFingerprint = (stops: RoutePoint[]) =>
  stops.map((s) => `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`).join(";");

/** Great-circle distance in km — the fallback leg length, as on the server. */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) {
  const R = 6371;
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

